import { deserializeWorkflowValue, getWorkflowDef, parseWorkflowDurationMs } from "@gencow/core";
import type { GencowCtx, WorkflowCtx, WorkflowDuration } from "@gencow/core";
import {
  claimWorkflowV2RunById,
  cancelClaimedWorkflowV2Run,
  completeWorkflowV2Run,
  deferWorkflowV2RunForCapacity,
  failWorkflowV2Run,
  getWorkflowV2CapacitySnapshot,
  getWorkflowV2Run,
  heartbeatWorkflowV2Run,
  markWorkflowV2RunCurrentStep,
  pauseWorkflowV2Run,
  requeueWorkflowV2Run,
  type WorkflowV2DbLike,
  type WorkflowV2RunRow,
} from "./workflow-kernel-v2-store.js";
import { decideWorkflowV2Capacity, type WorkflowV2CapacityPolicy } from "./workflow-kernel-v2-capacity.js";
import {
  claimWorkflowV2Step,
  completeWorkflowV2Step,
  failWorkflowV2Step,
  getWorkflowV2Step,
} from "./workflow-kernel-v2-steps.js";
import {
  claimWorkflowV2Signal,
  createWorkflowV2Timer,
  getWorkflowV2TimerByDedupe,
} from "./workflow-kernel-v2-durable.js";
import {
  mirrorWorkflowV2RunClaimed,
  mirrorWorkflowV2RunCompleted,
  mirrorWorkflowV2RunFailed,
  mirrorWorkflowV2RunPending,
  mirrorWorkflowV2StepCompleted,
  mirrorWorkflowV2StepFailed,
  mirrorWorkflowV2StepRunning,
} from "./workflow-kernel-v2-read-model.js";
import { attachWorkflowDocumentServices } from "./server-document-runtime.js";

export type ResumeWorkflowV2Result =
  | { status: "not_found"; runId: string }
  | { status: "skipped"; runId: string; workflowName: string }
  | { status: "sleeping"; runId: string; workflowName: string; stepKey: string; wakeAt: string }
  | {
      status: "waiting";
      runId: string;
      workflowName: string;
      stepKey: string;
      eventName: string;
      wakeAt?: string;
    }
  | { status: "completed"; runId: string; workflowName: string }
  | { status: "cancelled"; runId: string; workflowName: string }
  | {
      status: "requeued";
      runId: string;
      workflowName: string;
      retryCount: number;
      nextDelayMs: number;
      errorCode: string;
    }
  | {
      status: "capacity_deferred";
      runId: string;
      workflowName: string;
      reason: string;
      signal: string;
      nextDelayMs: number;
    }
  | { status: "failed"; runId: string; workflowName: string; errorCode: string; error: string };

export type ResumeWorkflowV2Options = {
  db: WorkflowV2DbLike;
  runId: string;
  workerId: string;
  leaseMs?: number;
  capacityPolicy?: WorkflowV2CapacityPolicy;
  buildCtx: () => GencowCtx;
};

function errorToJson(error: unknown): { message: string; name?: string } {
  if (error instanceof Error) {
    return { message: error.message, name: error.name };
  }
  return { message: String(error) };
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function storedRunErrorMessage(error: unknown): string {
  const decoded = parseJsonField(error);
  if (
    decoded &&
    typeof decoded === "object" &&
    typeof (decoded as { message?: unknown }).message === "string"
  ) {
    return (decoded as { message: string }).message;
  }
  try {
    return JSON.stringify(decoded);
  } catch {
    return String(decoded);
  }
}

function parseJsonField(value: unknown): unknown {
  if (typeof value !== "string") {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function decodeWorkflowV2StoredValue(value: unknown): unknown {
  return deserializeWorkflowValue(parseJsonField(value));
}

function restoreWorkflowAuth(baseAuth: GencowCtx["auth"], workflowUserId: string | null): GencowCtx["auth"] {
  if (baseAuth.getUserIdentity()) {
    return baseAuth;
  }
  if (!workflowUserId) {
    return baseAuth;
  }

  const restoredUser = {
    id: workflowUserId,
    email: `${workflowUserId}@workflow.local`,
  };

  return {
    getUserIdentity: () => restoredUser,
    requireAuth: () => restoredUser,
  };
}

function isTerminalStatus(status: WorkflowV2RunRow["status"]): boolean {
  return status === "completed" || status === "failed" || status === "cancelled";
}

function computeWorkflowV2RetryDelayMs(nextRetryCount: number): number {
  return 30_000 * 3 ** Math.max(0, nextRetryCount - 1);
}

function startWorkflowV2Heartbeat(options: {
  db: WorkflowV2DbLike;
  run: WorkflowV2RunRow;
  leaseMs: number;
}): () => void {
  const intervalMs = Math.max(1_000, Math.floor(options.leaseMs / 3));
  const interval = setInterval(() => {
    void heartbeatWorkflowV2Run({
      db: options.db,
      runId: options.run.id,
      leaseOwner: options.run.lease_owner ?? "",
      leaseEpoch: options.run.lease_epoch,
      leaseMs: options.leaseMs,
    });
  }, intervalMs);
  if (typeof (interval as { unref?: () => void }).unref === "function") {
    (interval as { unref: () => void }).unref();
  }
  return () => clearInterval(interval);
}

async function deferWorkflowV2RunIfCapacityExhausted(options: {
  db: WorkflowV2DbLike;
  runId: string;
  policy: WorkflowV2CapacityPolicy | undefined;
}): Promise<Extract<ResumeWorkflowV2Result, { status: "capacity_deferred" }> | null> {
  if (!options.policy) return null;
  const existing = await getWorkflowV2Run(options.db, options.runId);
  if (!existing || existing.status !== "queued") return null;
  const now = new Date();
  const runnableAtMs = new Date(existing.runnable_at).getTime();
  if (Number.isFinite(runnableAtMs) && runnableAtMs > now.getTime()) return null;

  const snapshot = await getWorkflowV2CapacitySnapshot(options.db);
  const decision = decideWorkflowV2Capacity({
    candidate: {
      id: existing.id,
      userId: existing.user_id,
      workflowName: existing.workflow_name,
      priority: existing.priority,
      runnableAt: existing.runnable_at,
    },
    snapshot: {
      ...snapshot,
      now,
    },
    policy: options.policy,
  });
  if (decision.allowed) return null;

  const deferred = await deferWorkflowV2RunForCapacity({
    db: options.db,
    runId: existing.id,
    retryAfterMs: decision.retryAfterMs,
    reason: decision.reason,
    signal: decision.signal,
  });
  if (!deferred) return null;

  await mirrorWorkflowV2RunPending({
    db: options.db,
    runId: deferred.id,
    stepKey: deferred.current_step_key,
  });

  return {
    status: "capacity_deferred",
    runId: deferred.id,
    workflowName: deferred.workflow_name,
    reason: decision.reason,
    signal: decision.signal,
    nextDelayMs: decision.retryAfterMs,
  };
}

class WorkflowV2PauseSignal extends Error {
  constructor(
    readonly status: "sleeping" | "waiting",
    readonly stepKey: string,
    readonly wakeAt: string | null,
    readonly eventName: string | null,
  ) {
    super(`workflow v2 ${status} at ${stepKey}`);
    this.name = "WorkflowV2PauseSignal";
  }
}

class WorkflowV2CancelledSignal extends Error {
  constructor() {
    super("Cancelled by user");
    this.name = "WorkflowV2CancelledSignal";
  }
}

function buildWorkflowV2Ctx(options: {
  baseCtx: GencowCtx;
  db: WorkflowV2DbLike;
  run: WorkflowV2RunRow;
  leaseMs: number;
}): WorkflowCtx {
  let stepIndex = 0;
  let sleepIndex = 0;
  let waitIndex = 0;

  const step = async <TResult>(stepKey: string, fn: () => Promise<TResult>): Promise<TResult> => {
    stepIndex += 1;
    const runBeforeStep = await getWorkflowV2Run(options.db, options.run.id);
    if (runBeforeStep?.cancel_requested_at) {
      throw new WorkflowV2CancelledSignal();
    }
    const existing = await getWorkflowV2Step(options.db, options.run.id, stepKey);
    if (existing?.status === "completed") {
      return decodeWorkflowV2StoredValue(existing.output_json) as TResult;
    }

    const claimedStep = await claimWorkflowV2Step({
      db: options.db,
      runId: options.run.id,
      stepKey,
      stepIndex,
      workerId: options.run.lease_owner ?? "",
      leaseEpoch: options.run.lease_epoch,
      leaseMs: options.leaseMs,
    });
    if (!claimedStep) {
      const refreshed = await getWorkflowV2Step(options.db, options.run.id, stepKey);
      if (refreshed?.status === "completed") {
        return decodeWorkflowV2StoredValue(refreshed.output_json) as TResult;
      }
      throw new Error(`workflow v2 step "${stepKey}" could not be claimed`);
    }

    await markWorkflowV2RunCurrentStep({
      db: options.db,
      runId: options.run.id,
      leaseOwner: options.run.lease_owner ?? "",
      leaseEpoch: options.run.lease_epoch,
      stepKey,
    });
    await mirrorWorkflowV2StepRunning({ db: options.db, runId: options.run.id, stepKey });

    try {
      const output = await fn();
      const committed = await completeWorkflowV2Step({
        db: options.db,
        runId: options.run.id,
        stepKey,
        leaseOwner: options.run.lease_owner ?? "",
        leaseEpoch: options.run.lease_epoch,
        output,
      });
      if (!committed) {
        throw new Error(`workflow v2 step "${stepKey}" lost its lease before checkpoint commit`);
      }
      await mirrorWorkflowV2StepCompleted({ db: options.db, runId: options.run.id, stepKey, output });
      return output;
    } catch (error) {
      const errorJson = errorToJson(error);
      await failWorkflowV2Step({
        db: options.db,
        runId: options.run.id,
        stepKey,
        leaseOwner: options.run.lease_owner ?? "",
        leaseEpoch: options.run.lease_epoch,
        error: errorJson,
        errorCode: "step_error",
      });
      await mirrorWorkflowV2StepFailed({ db: options.db, runId: options.run.id, stepKey, error: errorJson });
      throw error;
    }
  };

  const sleep: WorkflowCtx["sleep"] = async (duration) => {
    sleepIndex += 1;
    const stepKey = `sleep#${sleepIndex}`;
    const existingTimer = await getWorkflowV2TimerByDedupe({
      db: options.db,
      runId: options.run.id,
      dedupeKey: stepKey,
    });
    if (existingTimer?.status === "fired") return;

    const durationMs = parseWorkflowDurationMs(duration, "wf.sleep() duration");
    const wakeAt = new Date(Date.now() + durationMs).toISOString();
    await createWorkflowV2Timer({
      db: options.db,
      id: `${options.run.id}:${stepKey}`,
      runId: options.run.id,
      kind: "sleep",
      fireAt: wakeAt,
      dedupeKey: stepKey,
    });
    const paused = await pauseWorkflowV2Run({
      db: options.db,
      runId: options.run.id,
      leaseOwner: options.run.lease_owner ?? "",
      leaseEpoch: options.run.lease_epoch,
      status: "sleeping",
      currentStepKey: stepKey,
      runnableAt: wakeAt,
    });
    if (!paused) {
      throw new Error(`workflow v2 run "${options.run.id}" lost its lease before sleep checkpoint`);
    }
    await mirrorWorkflowV2RunPending({ db: options.db, runId: options.run.id, stepKey });
    throw new WorkflowV2PauseSignal("sleeping", stepKey, wakeAt, null);
  };

  const waitForEvent: WorkflowCtx["waitForEvent"] = async <TPayload = unknown>(
    eventName: string,
    timeout?: WorkflowDuration,
  ): Promise<TPayload> => {
    waitIndex += 1;
    const stepKey = `wait:${eventName}#${waitIndex}`;
    const signal = await claimWorkflowV2Signal({ db: options.db, runId: options.run.id, eventName });
    if (signal) return decodeWorkflowV2StoredValue(signal.payload_json) as TPayload;

    let timeoutAt: string | null = null;
    if (timeout != null) {
      const timeoutMs = parseWorkflowDurationMs(timeout, "wf.waitForEvent() timeout");
      timeoutAt = new Date(Date.now() + timeoutMs).toISOString();
      await createWorkflowV2Timer({
        db: options.db,
        id: `${options.run.id}:${stepKey}:timeout`,
        runId: options.run.id,
        kind: "wait_timeout",
        fireAt: timeoutAt,
        dedupeKey: `${stepKey}:timeout`,
      });
    }

    const paused = await pauseWorkflowV2Run({
      db: options.db,
      runId: options.run.id,
      leaseOwner: options.run.lease_owner ?? "",
      leaseEpoch: options.run.lease_epoch,
      status: "waiting",
      currentStepKey: stepKey,
      runnableAt: timeoutAt,
    });
    if (!paused) {
      throw new Error(`workflow v2 run "${options.run.id}" lost its lease before wait checkpoint`);
    }
    await mirrorWorkflowV2RunPending({ db: options.db, runId: options.run.id, stepKey });
    throw new WorkflowV2PauseSignal("waiting", stepKey, timeoutAt, eventName);
  };

  return {
    ...options.baseCtx,
    workflowId: options.run.id,
    workflowName: options.run.workflow_name,
    runId: options.run.id,
    attempt: options.run.attempt_count,
    stepAttempt: 0,
    services: attachWorkflowDocumentServices(options.baseCtx.services, {
      workflowId: options.run.id,
      workflowName: options.run.workflow_name,
      auth: options.baseCtx.auth,
    }),
    step,
    sleep,
    waitForEvent,
    parallel: async (tasks) => Promise.all(tasks.map((task) => task())) as any,
  };
}

export async function resumeWorkflowV2Run(options: ResumeWorkflowV2Options): Promise<ResumeWorkflowV2Result> {
  const leaseMs = options.leaseMs ?? 30_000;
  const capacityDeferred = await deferWorkflowV2RunIfCapacityExhausted({
    db: options.db,
    runId: options.runId,
    policy: options.capacityPolicy,
  });
  if (capacityDeferred) return capacityDeferred;

  const claimed = await claimWorkflowV2RunById({
    db: options.db,
    runId: options.runId,
    workerId: options.workerId,
    leaseMs,
    capacityPolicy: options.capacityPolicy,
  });

  if (!claimed) {
    const existing = await getWorkflowV2Run(options.db, options.runId);
    if (!existing) return { status: "not_found", runId: options.runId };
    if (existing.status === "completed") {
      await mirrorWorkflowV2RunCompleted({
        db: options.db,
        runId: existing.id,
        result: decodeWorkflowV2StoredValue(existing.result_json),
      });
      return { status: "completed", runId: existing.id, workflowName: existing.workflow_name };
    }
    if (existing.status === "failed") {
      const errorJson = parseJsonField(existing.error_json);
      await mirrorWorkflowV2RunFailed({
        db: options.db,
        runId: existing.id,
        retryCount: existing.retry_count,
        error: errorJson,
        errorCode: existing.error_code,
      });
      return {
        status: "failed",
        runId: existing.id,
        workflowName: existing.workflow_name,
        errorCode: existing.error_code ?? "workflow_failed",
        error: storedRunErrorMessage(existing.error_json),
      };
    }
    if (existing.status === "cancelled") {
      return { status: "cancelled", runId: existing.id, workflowName: existing.workflow_name };
    }
    const capacityDeferredAfterClaimRace = await deferWorkflowV2RunIfCapacityExhausted({
      db: options.db,
      runId: existing.id,
      policy: options.capacityPolicy,
    });
    if (capacityDeferredAfterClaimRace) return capacityDeferredAfterClaimRace;
    return {
      status: isTerminalStatus(existing.status) ? "skipped" : "skipped",
      runId: existing.id,
      workflowName: existing.workflow_name,
    };
  }

  await mirrorWorkflowV2RunClaimed(options.db, claimed);

  const def = getWorkflowDef(claimed.workflow_name);
  if (!def) {
    const error = `workflow "${claimed.workflow_name}" is not registered in this runtime`;
    const errorJson = { message: error };
    await failWorkflowV2Run({
      db: options.db,
      runId: claimed.id,
      leaseOwner: claimed.lease_owner ?? "",
      leaseEpoch: claimed.lease_epoch,
      error: errorJson,
      errorCode: "definition_missing",
    });
    await mirrorWorkflowV2RunFailed({
      db: options.db,
      runId: claimed.id,
      retryCount: claimed.retry_count,
      error: errorJson,
      errorCode: "definition_missing",
    });
    return {
      status: "failed",
      runId: claimed.id,
      workflowName: claimed.workflow_name,
      errorCode: "definition_missing",
      error,
    };
  }

  const baseCtx = options.buildCtx();
  const workflowAuth = restoreWorkflowAuth(baseCtx.auth, claimed.user_id);
  const wf = buildWorkflowV2Ctx({
    baseCtx: { ...baseCtx, auth: workflowAuth },
    db: options.db,
    run: claimed,
    leaseMs,
  });
  const workflowArgs = decodeWorkflowV2StoredValue(claimed.args_json);
  const stopHeartbeat = startWorkflowV2Heartbeat({ db: options.db, run: claimed, leaseMs });

  try {
    try {
      const result = await def.handler(wf, (workflowArgs ?? {}) as never);
      const runBeforeCompletion = await getWorkflowV2Run(options.db, claimed.id);
      if (runBeforeCompletion?.cancel_requested_at) {
        throw new WorkflowV2CancelledSignal();
      }
      const committed = await completeWorkflowV2Run({
        db: options.db,
        runId: claimed.id,
        leaseOwner: claimed.lease_owner ?? "",
        leaseEpoch: claimed.lease_epoch,
        result,
      });
      if (!committed) {
        const refreshed = await getWorkflowV2Run(options.db, claimed.id);
        if (refreshed?.cancel_requested_at) {
          throw new WorkflowV2CancelledSignal();
        }
        throw new Error(`workflow v2 run "${claimed.id}" lost its lease before completion commit`);
      }
      await mirrorWorkflowV2RunCompleted({ db: options.db, runId: claimed.id, result });
      return { status: "completed", runId: claimed.id, workflowName: claimed.workflow_name };
    } catch (error) {
      if (error instanceof WorkflowV2PauseSignal) {
        if (error.status === "sleeping") {
          return {
            status: "sleeping",
            runId: claimed.id,
            workflowName: claimed.workflow_name,
            stepKey: error.stepKey,
            wakeAt: error.wakeAt ?? "",
          };
        }
        return {
          status: "waiting",
          runId: claimed.id,
          workflowName: claimed.workflow_name,
          stepKey: error.stepKey,
          eventName: error.eventName ?? "",
          wakeAt: error.wakeAt ?? undefined,
        };
      }
      const errorJson = errorToJson(error);
      if (
        error instanceof WorkflowV2CancelledSignal ||
        (await getWorkflowV2Run(options.db, claimed.id))?.cancel_requested_at
      ) {
        const cancelled = await cancelClaimedWorkflowV2Run({
          db: options.db,
          runId: claimed.id,
          leaseOwner: claimed.lease_owner ?? "",
          leaseEpoch: claimed.lease_epoch,
          error: errorJson,
        });
        if (cancelled) {
          await mirrorWorkflowV2RunFailed({
            db: options.db,
            runId: claimed.id,
            retryCount: claimed.retry_count,
            error: errorJson,
            errorCode: "cancelled",
          });
        }
        return { status: "cancelled", runId: claimed.id, workflowName: claimed.workflow_name };
      }
      const nextRetry = claimed.retry_count + 1;
      if (nextRetry <= claimed.max_retries) {
        const nextDelayMs = computeWorkflowV2RetryDelayMs(nextRetry);
        const retryAt = new Date(Date.now() + nextDelayMs).toISOString();
        const requeued = await requeueWorkflowV2Run({
          db: options.db,
          runId: claimed.id,
          leaseOwner: claimed.lease_owner ?? "",
          leaseEpoch: claimed.lease_epoch,
          retryCount: nextRetry,
          error: errorJson,
          errorCode: "workflow_error",
          runnableAt: retryAt,
        });
        if (requeued) {
          await mirrorWorkflowV2RunPending({
            db: options.db,
            runId: claimed.id,
            stepKey: claimed.current_step_key,
            retryCount: nextRetry,
            error: errorJson,
            errorCode: "workflow_error",
          });
          return {
            status: "requeued",
            runId: claimed.id,
            workflowName: claimed.workflow_name,
            retryCount: nextRetry,
            nextDelayMs,
            errorCode: "workflow_error",
          };
        }
      }
      await failWorkflowV2Run({
        db: options.db,
        runId: claimed.id,
        leaseOwner: claimed.lease_owner ?? "",
        leaseEpoch: claimed.lease_epoch,
        error: errorJson,
        errorCode: "workflow_error",
      });
      await mirrorWorkflowV2RunFailed({
        db: options.db,
        runId: claimed.id,
        retryCount: nextRetry,
        error: errorJson,
        errorCode: "workflow_error",
      });
      return {
        status: "failed",
        runId: claimed.id,
        workflowName: claimed.workflow_name,
        errorCode: "workflow_error",
        error: errorMessage(error),
      };
    }
  } finally {
    stopHeartbeat();
  }
}
